// src/components/admin/DeliveryAreasComponent.js
import React, { useEffect, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { AdminShell } from './Sidebar';
import Modal, { ModalButton } from '../ui/Modal';
import {
  StatCard, RefreshButton, SearchField,
  TableCard, LoadingState, EmptyState, Pagination, BulkBar,
} from '../ui/CrmPage';

function desc(a, b, getVal) {
  const av = getVal(a); const bv = getVal(b);
  if (bv == null && av != null) return -1;
  if (av == null && bv != null) return 1;
  if (bv < av) return -1;
  if (bv > av) return 1;
  return 0;
}
const getComparator = (order, getter) =>
  order === 'desc' ? (a, b) => desc(a, b, getter) : (a, b) => -desc(a, b, getter);
const stableSort = (arr, comp) =>
  arr.map((el, i) => [el, i]).sort((a, b) => { const o = comp(a[0], b[0]); return o || a[1] - b[1]; }).map((el) => el[0]);

const sortIcon = (active, dir) => (
  <span className={`crmpage__sort ${active ? 'is-active' : ''}`} aria-hidden>
    {active ? (dir === 'asc' ? '▲' : '▼') : '↕'}
  </span>
);

export default function DeliveryAreasComponent() {
  const apiBase = useMemo(() => {
    const base = process.env.REACT_APP_API_URL || '';
    return base.endsWith('/') ? base : `${base}/`;
  }, []);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const [q, setQ] = useState('');
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');
  const [page, setPage] = useState(1);
  const rowsPerPage = 15;

  const [selected, setSelected] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ delivery_addresses: '', delivery_days: '' });
  const [formError, setFormError] = useState('');
  const [formSaving, setFormSaving] = useState(false);

  async function load() {
    setLoading(true); setError('');
    try {
      const r = await fetch(`${apiBase}v1/deliveryAreas?sortBy=id&orderBy=asc`);
      if (!r.ok) {
        const t = await r.text().catch(() => '');
        throw new Error(`HTTP ${r.status} ${r.statusText} ${t}`.trim());
      }
      const j = await r.json();
      setRows(Array.isArray(j) ? j : []);
      setSelected([]);
    } catch (e) {
      setError(e.message || 'Не удалось загрузить регионы доставки');
    } finally {
      setLoading(false);
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [apiBase, reloadKey]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) => {
      const addr = (r.delivery_addresses || '').toLowerCase();
      const days = (r.delivery_days || '').toLowerCase();
      const id = String(r.id || '');
      return addr.includes(term) || days.includes(term) || id.includes(term);
    });
  }, [rows, q]);

  const sortGetter = useMemo(() => {
    switch (orderBy) {
      case 'id':                 return (r) => Number(r.id) || 0;
      case 'delivery_addresses': return (r) => (r.delivery_addresses || '').toLowerCase();
      case 'delivery_days':      return (r) => (r.delivery_days || '').toLowerCase();
      default:                   return (r) => r[orderBy] ?? '';
    }
  }, [orderBy]);

  const sorted = useMemo(() => stableSort(filtered, getComparator(order, sortGetter)), [filtered, order, sortGetter]);
  const totalPages = Math.max(1, Math.ceil(sorted.length / rowsPerPage));
  const paged = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page]);
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const requestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const toggleSelect = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const toggleSelectAll = () => {
    const ids = paged.map((r) => r.id);
    const allSelected = ids.every((id) => selected.includes(id));
    setSelected(allSelected ? selected.filter((id) => !ids.includes(id)) : Array.from(new Set([...selected, ...ids])));
  };

  const handleDeleteSelected = async () => {
    if (selected.length === 0) return;
    try {
      await Promise.all(selected.map((id) => fetch(`${apiBase}v1/deliveryAreas/${id}`, { method: 'DELETE' })));
      setReloadKey((k) => k + 1);
    } catch (e) {
      setError(e.message || 'Не удалось удалить регионы');
    }
  };

  const openCreate = () => {
    setEditId(null);
    setForm({ delivery_addresses: '', delivery_days: '' });
    setFormError('');
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setEditId(row.id);
    setForm({ delivery_addresses: row.delivery_addresses || '', delivery_days: row.delivery_days || '' });
    setFormError('');
    setDialogOpen(true);
  };

  const saveForm = async () => {
    const da = form.delivery_addresses.trim();
    const dd = form.delivery_days.trim();
    if (!da || !dd) {
      setFormError('Заполните адреса и дни доставки.');
      return;
    }
    setFormSaving(true);
    try {
      const url = editId == null ? `${apiBase}v1/deliveryAreas` : `${apiBase}v1/deliveryAreas/${editId}`;
      const method = editId == null ? 'POST' : 'PUT';
      const r = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ delivery_addresses: da, delivery_days: dd }),
      });
      if (!r.ok) {
        const t = await r.text().catch(() => '');
        throw new Error(`HTTP ${r.status} ${t}`.trim());
      }
      setDialogOpen(false);
      setReloadKey((k) => k + 1);
    } catch (e) {
      setFormError(e.message || 'Ошибка сохранения');
    } finally {
      setFormSaving(false);
    }
  };

  return (
    <AdminShell
      breadcrumbs="Справочники"
      title="Регионы доставки"
      subtitle="Адреса и дни доставки"
      actions={
        <button type="button" className="adminbtn adminbtn--primary" onClick={openCreate}>
          <AddIcon sx={{ fontSize: 18 }} />
          Добавить
        </button>
      }
    >
      <div className="crmpage crmpage--embedded">
        <div className="crmpage__main">
          <section className="crmpage__stats crmpage__stats--compact">
            <StatCard label="Всего регионов" value={rows.length} accent="neutral" />
            <StatCard label="Отфильтровано" value={filtered.length} accent="brand" />
            <RefreshButton onClick={() => setReloadKey((k) => k + 1)} />
          </section>

          <section className="crmpage__filters">
            <SearchField
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="Поиск: ID, адрес, день…"
            />
          </section>

          {error && <div className="crmpage__alert crmpage__alert--error">{error}</div>}

          <TableCard>
            {loading ? (
              <LoadingState text="Загружаем регионы…" />
            ) : paged.length === 0 ? (
              <EmptyState title="Ничего не найдено" text={q ? 'Очистите поиск.' : 'Добавьте первый регион доставки.'} />
            ) : (
              <>
                <div className="crmpage__table-wrap">
                  <table className="crmpage__table">
                    <colgroup>
                      <col style={{ width: 40 }} />
                      <col style={{ width: 90 }} />
                      <col />
                      <col style={{ width: 220 }} />
                      <col style={{ width: 64 }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="crmpage__th-check">
                          <input
                            type="checkbox"
                            className="crmpage__check"
                            checked={paged.length > 0 && paged.every((r) => selected.includes(r.id))}
                            onChange={toggleSelectAll}
                            aria-label="Выделить все"
                          />
                        </th>
                        <th onClick={() => requestSort('id')} style={{ cursor: 'pointer' }}>ID {sortIcon(orderBy === 'id', order)}</th>
                        <th onClick={() => requestSort('delivery_addresses')} style={{ cursor: 'pointer' }}>Адреса доставки {sortIcon(orderBy === 'delivery_addresses', order)}</th>
                        <th onClick={() => requestSort('delivery_days')} style={{ cursor: 'pointer' }}>Дни доставки {sortIcon(orderBy === 'delivery_days', order)}</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {paged.map((row) => {
                        const checked = selected.includes(row.id);
                        return (
                          <tr
                            key={row.id}
                            className={`crmpage__row ${checked ? 'crmpage__row--selected' : ''}`}
                            onClick={() => openEdit(row)}
                          >
                            <td className="crmpage__td-check" onClick={(e) => { e.stopPropagation(); toggleSelect(row.id); }}>
                              <input type="checkbox" className="crmpage__check" checked={checked} onChange={() => toggleSelect(row.id)} />
                            </td>
                            <td className="crmpage__date">{row.id}</td>
                            <td className="crmpage__primary" title={row.delivery_addresses || ''}>
                              {row.delivery_addresses || '—'}
                            </td>
                            <td className="crmpage__muted">{row.delivery_days || '—'}</td>
                            <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                className="adminbtn adminbtn--icon"
                                title="Редактировать"
                                onClick={() => openEdit(row)}
                              ><EditIcon sx={{ fontSize: 18 }} /></button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Pagination current={page} total={totalPages} onChange={setPage} />
              </>
            )}
          </TableCard>

          <BulkBar count={selected.length} onClear={() => setSelected([])}>
            <button type="button" className="crmpage__btn crmpage__btn--danger" onClick={handleDeleteSelected}>
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
              Удалить выбранные
            </button>
          </BulkBar>
        </div>
      </div>

      <Modal
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editId == null ? 'Добавить регион доставки' : `Регион доставки № ${editId}`}
        subtitle="Адреса и дни"
        size="md"
        footer={
          <>
            <ModalButton variant="secondary" onClick={() => setDialogOpen(false)}>Отмена</ModalButton>
            <ModalButton variant="primary" onClick={saveForm} disabled={formSaving}>
              {formSaving ? 'Сохраняем…' : 'Сохранить'}
            </ModalButton>
          </>
        }
      >
        {formError && <div className="crmpage__alert crmpage__alert--error" style={{ marginBottom: 12 }}>{formError}</div>}
        <div className="adminform__grid adminform__grid--full">
          <label className="adminform__field">
            <span className="adminform__label">Адреса доставки</span>
            <textarea
              className="adminform__textarea"
              value={form.delivery_addresses}
              onChange={(e) => setForm((f) => ({ ...f, delivery_addresses: e.target.value }))}
              rows={3}
              placeholder="Астана, Алматы, Шымкент…"
            />
          </label>
          <label className="adminform__field">
            <span className="adminform__label">Дни доставки</span>
            <input
              className="adminform__input"
              value={form.delivery_days}
              onChange={(e) => setForm((f) => ({ ...f, delivery_days: e.target.value }))}
              placeholder="Например: Пн, Ср, Пт"
            />
          </label>
        </div>
      </Modal>
    </AdminShell>
  );
}
